import type {QueryClient} from '@tanstack/react-query'
import {useQueryClient} from '@tanstack/react-query'
import {useCallback} from 'react'

import type {
  MemexItem,
  PaginatedGroupedItemsDataBase,
  PaginatedMemexItemsDataBase,
} from '../../../api/memex-items/contracts'
import {
  getQueryDataForMemexGroupsPage,
  getQueryDataForMemexItemsPage,
  setQueryDataForMemexGroupsPage,
  setQueryDataForMemexItemsPage,
} from '../query-client-api'
import {buildMemexItemsPageQueryKey, type GroupId, groupIdForUngroupedData} from './query-keys'
import {
  type MemexGroupsPageQueryData,
  type MemexItemsPageQueryData,
  type MemexPageQueryData,
  type PageParam,
  pageParamForInitialPage,
  pageParamForNextPlaceholder,
  type PageParamsByGroupId,
  type PaginatedMemexItemsQueryVariables,
} from './types'
import {useMemexItemsActiveQueriesMetadata} from './use-memex-items-active-queries-metadata'

// The next_placeholder should always be the last page param, so hasNextPage must be false.
export const nextPlaceholderPageInfo = {hasNextPage: false, hasPreviousPage: false}

export function useNextPlaceholderQuery(variables: PaginatedMemexItemsQueryVariables) {
  const {getActiveQueriesMetadata, setActiveQueriesMetadata} = useMemexItemsActiveQueriesMetadata(variables)
  const queryClient = useQueryClient()

  const setUpNextPlaceholderQueries = useCallback(() => {
    const currentActiveQueriesMetadata = getActiveQueriesMetadata()
    const currentDataIsGrouped = 'groups' in currentActiveQueriesMetadata
    if (currentDataIsGrouped) {
      const nextPlaceholderMetadataGroups: PageParamsByGroupId = {}
      // Copy all items for each group into a placeholder query
      for (const [groupId, pageParams] of Object.entries(currentActiveQueriesMetadata.groups)) {
        const nextPlaceholderNodesForGroup = pageParams.flatMap(
          pageParam => getQueryDataForMemexItemsPage(queryClient, variables, groupId, pageParam)?.nodes || [],
        )
        const nextPlaceholderDataForGroup: MemexItemsPageQueryData = {
          nodes: nextPlaceholderNodesForGroup,
          pageInfo: nextPlaceholderPageInfo,
        }
        // Set query data for the placeholder page for this group
        setQueryDataForNextPlaceholder(queryClient, variables, groupId, nextPlaceholderDataForGroup)

        // Set the updated pageParams metadata for this group: ['next_placeholder']
        nextPlaceholderMetadataGroups[groupId] = [pageParamForNextPlaceholder]
      }

      // Build a top-level group page placeholder query
      const nextPlaceholderGroups = currentActiveQueriesMetadata.pageParams.flatMap(
        pageParam => getQueryDataForMemexGroupsPage(queryClient, variables, pageParam)?.groups || [],
      )
      const nextPlaceholderData: MemexGroupsPageQueryData = {
        groups: nextPlaceholderGroups,
        pageInfo: nextPlaceholderPageInfo,
      }
      // Set query data for the placeholder groups page
      setQueryDataForNextPlaceholder(queryClient, variables, groupIdForUngroupedData, nextPlaceholderData)

      // Update the active queries metadata; top-level and per group:
      // groups: all groups, each with pageParams: ['next_placeholder']
      // pageParams: [undefined, 'next_placeholder']
      setActiveQueriesMetadata({
        groups: nextPlaceholderMetadataGroups,
        pageParams: [pageParamForInitialPage, pageParamForNextPlaceholder],
      })
    } else {
      // Build a top-level items page placeholder query
      const nextPlaceholderNodes = currentActiveQueriesMetadata.pageParams.flatMap(
        pageParam =>
          getQueryDataForMemexItemsPage(queryClient, variables, groupIdForUngroupedData, pageParam)?.nodes || [],
      )
      // Move all data into the `afterPlaceholderData` query
      const nextPlaceholderData: MemexItemsPageQueryData = {
        nodes: nextPlaceholderNodes,
        pageInfo: nextPlaceholderPageInfo,
      }
      // Set query data for the next placeholder page
      setQueryDataForNextPlaceholder(queryClient, variables, groupIdForUngroupedData, nextPlaceholderData)
      // Update the active queries metadata to reflect the new page params: [undefined, 'next_placeholder']
      setActiveQueriesMetadata({pageParams: [pageParamForInitialPage, pageParamForNextPlaceholder]})
    }
  }, [getActiveQueriesMetadata, queryClient, setActiveQueriesMetadata, variables])

  const getUpdatedPageParams = useCallback(
    (
      pageParams: Array<PageParam>,
      groupId: GroupId,
      newItemsData: PaginatedMemexItemsDataBase<MemexItem> | undefined,
    ): Array<PageParam> => {
      if (pageParams[pageParams.length - 1] === pageParamForNextPlaceholder) {
        // We have some data in the `next_placeholder` query, so let's update the data, and potentially
        // add a new query for the next page.
        const currentNextPlaceholderData = getQueryDataForMemexItemsPage(
          queryClient,
          variables,
          groupId,
          pageParamForNextPlaceholder,
        )
        const pageParamsWithoutNextPlaceholder = pageParams.slice(0, pageParams.length - 1)
        const newNextPlaceholderDataNodes = currentNextPlaceholderData?.nodes.slice(newItemsData?.nodes.length)
        if (
          newNextPlaceholderDataNodes?.length &&
          newItemsData?.pageInfo.hasNextPage &&
          newItemsData?.pageInfo.endCursor
        ) {
          const newNextPlaceholderData: MemexItemsPageQueryData = {
            nodes: newNextPlaceholderDataNodes,
            pageInfo: nextPlaceholderPageInfo,
          }
          const newPageParams: Array<PageParam> = [
            ...pageParamsWithoutNextPlaceholder,
            {after: newItemsData?.pageInfo.endCursor},
            pageParamForNextPlaceholder,
          ]
          // Set query data for the next placeholder page
          setQueryDataForMemexItemsPage(
            queryClient,
            variables,
            groupId,
            pageParamForNextPlaceholder,
            newNextPlaceholderData,
          )
          return newPageParams
        } else {
          // We have no more data in the next placeholder query, so let's remove it.
          queryClient.removeQueries(buildMemexItemsPageQueryKey(variables, groupId, pageParamForNextPlaceholder))
          return pageParamsWithoutNextPlaceholder
        }
      }
      return pageParams
    },
    [queryClient, variables],
  )

  const updateMemexItemsNextPlaceholderQuery = useCallback(
    (response: PaginatedMemexItemsDataBase<MemexItem>, groupId: GroupId) => {
      const currentActiveQueriesMetadata = getActiveQueriesMetadata()
      const isResultForGroup = groupId !== groupIdForUngroupedData && 'groups' in currentActiveQueriesMetadata
      const pageParams = isResultForGroup
        ? currentActiveQueriesMetadata.groups[groupId] || []
        : currentActiveQueriesMetadata.pageParams
      if (pageParams[pageParams.length - 1] !== pageParamForNextPlaceholder) {
        // Nothing to handle, we can return early
        return
      }
      const newPageParams = getUpdatedPageParams(pageParams, groupId, response)
      if (isResultForGroup) {
        const newGroups = {...currentActiveQueriesMetadata.groups}
        newGroups[groupId] = newPageParams
        setActiveQueriesMetadata({
          ...currentActiveQueriesMetadata,
          groups: newGroups,
        })
      } else {
        setActiveQueriesMetadata({
          ...currentActiveQueriesMetadata,
          pageParams: newPageParams,
        })
      }
    },
    [getActiveQueriesMetadata, getUpdatedPageParams, setActiveQueriesMetadata],
  )

  const updateMemexGroupsNextPlaceholderQuery = useCallback(
    (response: PaginatedGroupedItemsDataBase<MemexItem>) => {
      const currentActiveQueriesMetadata = getActiveQueriesMetadata()

      // First handle any placeholder data for each individual group
      const originalPerGroupPageParams =
        'groups' in currentActiveQueriesMetadata ? currentActiveQueriesMetadata.groups : {}
      const newPerGroupPageParams = {...originalPerGroupPageParams}
      for (const group of response.groups) {
        const pageParams = originalPerGroupPageParams[group.groupId] || []
        if (pageParams.length === 0 || pageParams[0] !== pageParamForInitialPage) {
          // A grouped response contains the first page for multiple groups,
          // so we need to update this group's pageParams metadata to reflect that
          // this first page of data is now available.
          pageParams.unshift(pageParamForInitialPage)
        }
        const newGroupPageParams = getUpdatedPageParams(pageParams, group.groupId, group.items)
        newPerGroupPageParams[group.groupId] = newGroupPageParams || []
      }

      // Then handle any placeholder data for the top level groups
      const pageParams = currentActiveQueriesMetadata.pageParams
      if (pageParams[pageParams.length - 1] === pageParamForNextPlaceholder) {
        // We have some data in the `next_placeholder` query, so lets update the data, and potentially
        // add a new query for the next page.
        const currentNextPlaceholderData = getQueryDataForMemexGroupsPage(
          queryClient,
          variables,
          pageParamForNextPlaceholder,
        )
        const pageParamsWithoutNextPlaceholder = pageParams.slice(0, pageParams.length - 1)
        const newNextPlaceholderDataGroups = currentNextPlaceholderData?.groups.slice(response.groups.length)
        if (newNextPlaceholderDataGroups?.length && response.pageInfo.hasNextPage && response.pageInfo.endCursor) {
          const newNextPlaceholderData: MemexGroupsPageQueryData = {
            groups: newNextPlaceholderDataGroups,
            pageInfo: nextPlaceholderPageInfo,
          }
          const newPageParams: Array<PageParam> = [
            ...pageParamsWithoutNextPlaceholder,
            {after: response.pageInfo.endCursor},
            pageParamForNextPlaceholder,
          ]

          setActiveQueriesMetadata({
            groups: newPerGroupPageParams,
            pageParams: newPageParams,
          })

          // Set query data for the next placeholder page
          setQueryDataForMemexGroupsPage(queryClient, variables, pageParamForNextPlaceholder, newNextPlaceholderData)
        } else {
          // We have no more data in the next placeholder query, so let's remove it.
          queryClient.removeQueries(
            buildMemexItemsPageQueryKey(variables, groupIdForUngroupedData, pageParamForNextPlaceholder),
          )
          setActiveQueriesMetadata({
            groups: newPerGroupPageParams,
            pageParams: pageParamsWithoutNextPlaceholder,
          })
        }
      } else {
        setActiveQueriesMetadata({
          ...currentActiveQueriesMetadata,
          groups: newPerGroupPageParams,
        })
      }
    },
    [getActiveQueriesMetadata, getUpdatedPageParams, queryClient, setActiveQueriesMetadata, variables],
  )

  return {setUpNextPlaceholderQueries, updateMemexGroupsNextPlaceholderQuery, updateMemexItemsNextPlaceholderQuery}
}

function setQueryDataForNextPlaceholder(
  queryClient: QueryClient,
  variables: PaginatedMemexItemsQueryVariables,
  groupId: GroupId,
  nextPlaceholderData: MemexPageQueryData,
) {
  if ('groups' in nextPlaceholderData) {
    setQueryDataForMemexGroupsPage(queryClient, variables, pageParamForNextPlaceholder, nextPlaceholderData)
  } else {
    setQueryDataForMemexItemsPage(queryClient, variables, groupId, pageParamForNextPlaceholder, nextPlaceholderData)
  }
}
